import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { FileText, Search, Calendar, Loader2, Eye } from "lucide-react";
import { API_ENDPOINTS } from "../config/api";
import apiRequest from "../utils/api";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

interface StudentRecord {
  id: string;
  uploadType: number;
  bucketKey: string;
  documentNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AnalysisComponentResult {
  componentType: string;
  status: string;
  analysisData: any;
  pdfKey: string;
  errorMessage?: string;
  processingTimeMs: number;
}

interface AnalysisStatus {
  sessionId: string | null;
  status: "idle" | "processing" | "completed" | "failed";
  message?: string;
  completedAt: string | null;
  analysisResults?: Record<string, AnalysisComponentResult>; // 분석 결과 데이터
}

interface RecordWithAnalysis extends StudentRecord {
  analysis?: AnalysisStatus;
}

export const StudentRecords: React.FC = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState<RecordWithAnalysis[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<RecordWithAnalysis[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // 학생부 파일 목록 조회
  useEffect(() => {
    fetchStudentRecords();
  }, []);

  // 검색 필터링
  useEffect(() => {
    const filtered = records.filter(
      (record) =>
        record.bucketKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.documentNumber &&
          record.documentNumber
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        record.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredRecords(filtered);
  }, [records, searchTerm]);

  const fetchStudentRecords = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(API_ENDPOINTS.STUDENT_RECORDS, {
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRecords(data.data || []);
        }
      }
    } catch (error) {
      console.error("학생부 파일 목록 조회 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const getUploadTypeLabel = (uploadType: number) => {
    const types: Record<number, string> = {
      1: "일반",
      2: "대량",
      3: "개별",
    };
    return types[uploadType] || `타입 ${uploadType}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFileNameFromBucketKey = (bucketKey: string) => {
    const parts = bucketKey.split("/");
    return parts[parts.length - 1] || bucketKey;
  };

  const getAnalysisStatusBadge = (record: RecordWithAnalysis) => {
    if (!record.analysis) {
      return null;
    }

    const status = record.analysis.status;

    if (status === "processing") {
      return (
        <Badge
          variant="outline"
          className="bg-yellow-50 text-yellow-700 border-yellow-300"
        >
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          분석 중
        </Badge>
      );
    }

    if (status === "completed") {
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-300"
        >
          완료
        </Badge>
      );
    }

    if (status === "failed") {
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-300"
        >
          실패
        </Badge>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FileText className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">학생부 파일 관리</h1>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">전체 파일</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{records.length}</div>
            <p className="text-xs text-muted-foreground">등록된 파일 수</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">일반 업로드</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {records.filter((r) => r.uploadType === 1).length}
            </div>
            <p className="text-xs text-muted-foreground">타입 1 파일</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">문서번호 있음</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {records.filter((r) => r.documentNumber !== null).length}
            </div>
            <p className="text-xs text-muted-foreground">문서번호 등록됨</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">최근 업로드</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {records.length > 0
                ? formatDate(
                    records.sort(
                      (a, b) =>
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime()
                    )[0].createdAt
                  ).split(" ")[0]
                : "-"}
            </div>
            <p className="text-xs text-muted-foreground">가장 최근 날짜</p>
          </CardContent>
        </Card>
      </div>

      {/* 검색 및 필터 */}
      <Card>
        <CardHeader>
          <CardTitle>파일 검색</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="파일명, 문서번호, ID로 검색..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              className="flex-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* 파일 목록 테이블 */}
      <Card>
        <CardHeader>
          <CardTitle>학생부 파일 목록</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">로딩 중...</div>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">
                {searchTerm
                  ? "검색 결과가 없습니다."
                  : "등록된 파일이 없습니다."}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">파일명</TableHead>
                    <TableHead className="w-32">업로드 타입</TableHead>
                    <TableHead className="w-40">문서번호</TableHead>
                    <TableHead className="w-48">Bucket Key</TableHead>
                    <TableHead className="w-32">분석 상태</TableHead>
                    <TableHead className="w-36">생성일</TableHead>
                    <TableHead className="w-36">수정일</TableHead>
                    <TableHead className="w-40">액션</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">
                        <button
                          onClick={() =>
                            navigate(`/student-records/${record.id}`)
                          }
                          className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate max-w-xs">
                            {getFileNameFromBucketKey(record.bucketKey)}
                          </span>
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {getUploadTypeLabel(record.uploadType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {record.documentNumber || (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground truncate block max-w-xs">
                          {record.bucketKey}
                        </span>
                      </TableCell>
                      <TableCell>
                        {getAnalysisStatusBadge(record) || (
                          <span className="text-xs text-muted-foreground">
                            -
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(record.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(record.updatedAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            navigate(`/student-records/${record.id}`)
                          }
                          className="flex items-center space-x-1"
                        >
                          <Eye className="h-3 w-3" />
                          <span className="text-xs">상세보기</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
